/**
 * TicketsPage — the admin console for Contact-Us submissions and product
 * feature requests filed from the /contact page. Two-pane layout: a filterable
 * list on the left (type + status + search) and the selected ticket on the
 * right with its full message, status controls and private staff notes.
 *
 * Unlike Live Chat (real-time back-and-forth), a ticket is a one-shot inbound
 * message — staff reply by email, then move the ticket through
 * open → in progress → resolved → closed here.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import Icon from './Icon';

const LIST_POLL_MS = 15000;

const STATUS_META = {
  open:        { label: 'Open',        cls: 'bg-tea-100 text-tea-700' },
  in_progress: { label: 'In progress', cls: 'bg-amber-100 text-amber-700' },
  resolved:    { label: 'Resolved',    cls: 'bg-green-100 text-green-700' },
  closed:      { label: 'Closed',      cls: 'bg-gray-100 text-gray-600' },
};
const STATUSES = ['open', 'in_progress', 'resolved', 'closed'];

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [typeFilter, setTypeFilter] = useState('all');   // all | support | feature
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [loadingList, setLoadingList] = useState(true);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/tickets', {
        params: { type: typeFilter, status: statusFilter, search: search || undefined },
      });
      setTickets(data.tickets || []);
    } catch {
      // transient — keep the last good list
    } finally {
      setLoadingList(false);
    }
  }, [typeFilter, statusFilter, search]);

  useEffect(() => {
    loadList();
    const id = setInterval(loadList, LIST_POLL_MS);
    return () => clearInterval(id);
  }, [loadList]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const { data } = await api.get(`/admin/tickets/${id}`);
      setDetail(data.ticket);
      setNotes(data.ticket.adminNotes || '');
      // mark-as-read changes the badge — refresh the list quietly
      loadList();
    } catch {
      toast.error('Could not load ticket');
    }
  }, [loadList]);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const setStatus = async (status) => {
    if (!detail) return;
    try {
      const { data } = await api.patch(`/admin/tickets/${detail.id}`, { status });
      setDetail(data.ticket);
      loadList();
      toast.success(`Marked ${STATUS_META[status].label.toLowerCase()}`);
    } catch {
      toast.error('Could not update status');
    }
  };

  const saveNotes = async () => {
    if (!detail) return;
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/admin/tickets/${detail.id}`, { adminNotes: notes });
      setDetail(data.ticket);
      toast.success('Notes saved');
    } catch {
      toast.error('Could not save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const remove = async () => {
    if (!detail || !window.confirm(`Delete ticket ${detail.reference}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/tickets/${detail.id}`);
      toast.success('Ticket deleted');
      setSelectedId(null);
      setDetail(null);
      loadList();
    } catch {
      toast.error('Could not delete ticket');
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-extrabold text-gray-900">Tickets & Feature Requests</h1>
        <p className="text-sm text-gray-500 mt-1">Contact-Us submissions and ideas from the storefront.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex bg-gray-100 rounded-lg p-1">
          {[['all', 'All'], ['support', 'Support'], ['feature', 'Features']].map(([v, l]) => (
            <button key={v} onClick={() => setTypeFilter(v)}
              className={'px-3 py-1.5 text-sm font-semibold rounded-md transition-colors ' +
                (typeFilter === v ? 'bg-white text-tea-700 shadow-sm' : 'text-gray-500')}>
              {l}
            </button>
          ))}
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tea-400">
          <option value="all">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search reference, name, email, subject…"
          className="flex-1 min-w-[200px] text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-tea-400" />
      </div>

      <div className="grid lg:grid-cols-[minmax(0,380px)_1fr] gap-5">
        {/* List */}
        <div className="border border-gray-200 rounded-xl overflow-hidden bg-white max-h-[70vh] overflow-y-auto">
          {loadingList ? (
            <div className="p-8 text-center text-gray-400 text-sm">Loading…</div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">No tickets match.</div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {tickets.map((t) => (
                <li key={t.id}>
                  <button onClick={() => setSelectedId(t.id)}
                    className={'w-full text-left px-4 py-3.5 hover:bg-gray-50 transition-colors ' +
                      (selectedId === t.id ? 'bg-tea-50' : '')}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="flex items-center gap-2 min-w-0">
                        {!t.read && <span className="w-2 h-2 rounded-full bg-tea-500 shrink-0" title="Unread" />}
                        <span className="font-mono text-xs font-bold text-gray-500 shrink-0">{t.reference}</span>
                        {t.type === 'feature' && <span className="text-[10px] font-bold uppercase text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded shrink-0">Idea</span>}
                      </span>
                      <span className={'text-[10px] font-bold uppercase px-2 py-0.5 rounded-full shrink-0 ' + STATUS_META[t.status].cls}>
                        {STATUS_META[t.status].label}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-800 text-sm truncate">{t.subject}</div>
                    <div className="text-xs text-gray-400 mt-0.5 truncate">
                      {t.name} · {t.email} · {timeAgo(t.createdAt)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="border border-gray-200 rounded-xl bg-white min-h-[400px]">
          {!detail ? (
            <div className="h-full flex flex-col items-center justify-center text-center p-10 text-gray-400">
              <Icon name="ticket" className="h-10 w-10 mb-3 text-gray-300" />
              <div className="text-sm">Select a ticket to view it.</div>
            </div>
          ) : (
            <div className="p-6">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-sm font-bold text-gray-500">{detail.reference}</span>
                    <span className={'text-[10px] font-bold uppercase px-2 py-0.5 rounded ' +
                      (detail.type === 'feature' ? 'text-amber-600 bg-amber-50' : 'text-tea-700 bg-tea-50')}>
                      {detail.type === 'feature' ? 'Feature request' : 'Support'}
                    </span>
                  </div>
                  <h2 className="text-lg font-extrabold text-gray-900 leading-snug">{detail.subject}</h2>
                </div>
                <button onClick={remove} className="text-xs text-red-500 hover:text-red-700 font-semibold shrink-0">Delete</button>
              </div>

              {/* Meta */}
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mb-5 bg-gray-50 rounded-xl p-4">
                <div><span className="text-gray-400">From:</span> <span className="font-medium text-gray-800">{detail.name}</span></div>
                <div className="truncate"><span className="text-gray-400">Email:</span> <a href={`mailto:${detail.email}`} className="font-medium text-tea-600 hover:underline">{detail.email}</a></div>
                {detail.category && <div><span className="text-gray-400">Topic:</span> <span className="font-medium text-gray-800">{detail.category}</span></div>}
                {detail.orderRef && <div><span className="text-gray-400">Order:</span> <span className="font-mono font-medium text-gray-800">{detail.orderRef}</span></div>}
                <div><span className="text-gray-400">Filed:</span> <span className="font-medium text-gray-800">{new Date(detail.createdAt).toLocaleString()}</span></div>
              </div>

              {/* Message */}
              <div className="mb-6">
                <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Message</div>
                <div className="bg-white border border-gray-200 rounded-xl p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {detail.message}
                </div>
                <a href={`mailto:${detail.email}?subject=Re: [${detail.reference}] ${encodeURIComponent(detail.subject)}`}
                  className="inline-block mt-3 btn-primary text-sm px-5 py-2.5">
                  Reply by email →
                </a>
              </div>

              {/* Status controls */}
              <div className="mb-6">
                <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Status</div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((s) => (
                    <button key={s} onClick={() => setStatus(s)}
                      className={'px-3 py-1.5 text-sm font-semibold rounded-lg border transition-colors ' +
                        (detail.status === s
                          ? 'border-tea-500 ' + STATUS_META[s].cls
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50')}>
                      {STATUS_META[s].label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Private notes */}
              <div>
                <div className="text-xs uppercase tracking-wide text-gray-400 font-semibold mb-2">Private notes (staff only)</div>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                  placeholder="Internal notes about this ticket…"
                  className="w-full text-sm border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-tea-400 resize-y" />
                <button onClick={saveNotes} disabled={savingNotes}
                  className="mt-2 btn-secondary text-sm px-5 py-2 disabled:opacity-50">
                  {savingNotes ? 'Saving…' : 'Save notes'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
